/**
 * Contrato neutral de la Recolección de Datos Operativos de IA.
 * No contiene tipos del navegador ni de Firebase para poder compartirse entre
 * la aplicación y Cloud Functions.
 */

export const OPERATIONAL_AI_SCHEMA_VERSION = "1.0.0";

export type OperationalPhaseId =
  | "identidad"
  | "empresa"
  | "protocolo"
  | "logistica"
  | "politicas"
  | "producto"
  | "producto_detalles"
  | "producto_comercial"
  | "estrategia"
  | "revision";

export type OperationalProfileStatus =
  | "sin_iniciar"
  | "en_progreso"
  | "en_revision"
  | "aprobado";

export type OperationalFieldStatus =
  | "propuesto"
  | "confirmado"
  | "corregido"
  | "requiere_revision";

export type OperationalFilledBy = "ia" | "dueno" | "mixto" | "inventario";
export type OperationalDataNature = "estatico" | "dinamico";
export type OperationalCriticality = "critico" | "recomendado" | "opcional";

export type OperationalJsonValue =
  | string
  | number
  | boolean
  | null
  | OperationalJsonValue[]
  | { [key: string]: OperationalJsonValue };

export interface OperationalEvidence {
  id: string;
  type: "mensaje" | "archivo" | "url" | "imagen" | "pdf" | "inventario";
  reference: string;
  label: string;
  excerpt?: string;
  page?: number;
}

export interface OperationalFieldEntry {
  key: string;
  value: OperationalJsonValue;
  status: OperationalFieldStatus;
  filledBy: OperationalFilledBy;
  sources: OperationalEvidence[];
  updatedAt: string;
  requiresConfirmation: boolean;
  nature: OperationalDataNature;
}

export interface OperationalAuditEntry {
  id: string;
  fieldKey: string;
  actor: "ia" | "dueno" | "sistema";
  action: "propuesto" | "confirmado" | "corregido" | "conflicto";
  occurredAt: string;
}

export interface OperationalPhaseDocument {
  schemaVersion: string;
  phaseId: OperationalPhaseId;
  nodeIds: string[];
  fields: Record<string, OperationalFieldEntry>;
  visited: boolean;
  completed: boolean;
  updatedAt: string;
  audit: OperationalAuditEntry[];
}

export interface OperationalProfile {
  schemaVersion: string;
  id: string;
  ownerId: string;
  phoneId: string;
  phoneLabel: string;
  productId: string | null;
  productName: string;
  productImageUrl: string | null;
  catalogImageUrls?: string[];
  country: string;
  salesTechnique: string;
  experience: "quetzal_ia_operativa_v3";
  status: OperationalProfileStatus;
  currentPhase: OperationalPhaseId;
  visitedPhases: OperationalPhaseId[];
  completeness: number;
  criticalMissing: string[];
  activationStatus: "pendiente_configuracion" | "lista_para_ventas";
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
}

export interface OperationalMessage {
  id: string;
  role: "dueno" | "asistente" | "sistema";
  text: string;
  state: "enviado" | "analizando" | "completado" | "pendiente_reintento" | "error";
  attachments: OperationalAttachment[];
  createdAt: string;
  provider?: "deepseek" | "gemini";
}

export interface OperationalAttachment {
  id: string;
  name: string;
  mimeType: "image/png" | "image/jpeg" | "image/webp" | "application/pdf";
  size: number;
  storagePath: string;
  downloadUrl?: string;
}

export interface OperationalFieldProposal {
  phaseId: Exclude<OperationalPhaseId, "revision">;
  fieldKey: string;
  value: OperationalJsonValue;
  ownerExplicit: boolean;
  conflict: boolean;
  evidenceIndexes: number[];
  evidenceExcerpt?: string;
}

export interface OperationalExtractionOutput {
  assistantMessage: string;
  proposals: OperationalFieldProposal[];
  nextCriticalQuestions: string[];
}

export interface ProcessOperationalCollectionRequest {
  requestId: string;
  workspaceId: string;
  message: string;
  attachments: OperationalAttachment[];
  currentPhase: OperationalPhaseId;
}

export interface ProcessOperationalCollectionResponse {
  requestId: string;
  status: "completed" | "pending_retry";
  assistantMessage: string;
  provider: "deepseek" | "gemini" | null;
  changedPhases: OperationalPhaseId[];
  completeness: number;
  criticalMissing: string[];
}

export interface TranscribeOperationalAudioRequest {
  audioBase64: string;
  mimeType: "audio/webm" | "audio/ogg" | "audio/mp4" | "audio/mpeg" | "audio/wav";
  durationMs: number;
  fieldKey?: string;
  fieldLabel?: string;
  fieldRole?: string;
  productContext?: Record<string, string>;
}

export interface TranscribeOperationalAudioResponse {
  transcript: string;
}

export interface ApproveOperationalCollectionRequest {
  workspaceId: string;
}

export interface ApproveOperationalCollectionResponse {
  approved: boolean;
  completeness: number;
  criticalMissing: string[];
  knowledgeNodeIds: string[];
  bindingId: string | null;
}

export interface DeleteOperationalWorkspaceRequest {
  workspaceId: string;
}

export interface DeleteOperationalWorkspaceResponse {
  deleted: true;
}

/** Puntero estable que conecta línea y producto con conocimiento aprobado. */
export interface OperationalAIBinding {
  schemaVersion: string;
  id: string;
  ownerId: string;
  phoneId: string;
  productId: string | null;
  profileId: string;
  knowledgeNodeIds: string[];
  knowledgePath: string;
  status: "aprobado";
  country: string;
  salesTechnique: string;
  approvedAt: string;
  updatedAt: string;
}

export interface CreateOperationalWorkspaceInput {
  catalogProducts?: import("./operational-ai.catalog").CatalogProduct[];
  phoneId: string;
  phoneLabel: string;
  productId: string | null;
  productName: string;
  productImageUrl: string | null;
  productCategory?: string;
  productSku?: string;
  productCurrency?: string;
  country: string;
  salesTechnique: string;
}

export interface OperationalCompleteness {
  percentage: number;
  criticalMissing: string[];
  completedPhases: OperationalPhaseId[];
}
